import React, { useState, useEffect } from "react";
import axios from "axios";
import EmojiPicker from "emoji-picker-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { buildApiUrl } from "../../config";  // ✅ Import config
import { getYearLabel } from "../../utils/yearUtils";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

// ✅ Configure custom font sizes for Quill
const Size = Quill.import('formats/size');
Size.whitelist = ['10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px'];
Quill.register(Size, true);

const CreatePost = ({ onClose, onPostCreated, authorInfo, studentForm, targetForms }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedForms, setSelectedForms] = useState(null); // nothing selected by default
  const [files, setFiles] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // Emoji picker state
  const [category, setCategory] = useState("school-announcement");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Stable blob preview URLs keyed by mediaFiles index; created/revoked via useEffect
  const [previewUrls, setPreviewUrls] = useState([]);

  // ✅ NEW: Pupil tagging states
  const [availablePupils, setAvailablePupils] = useState([]);
  const [taggedPupils, setTaggedPupils] = useState([]);
  const [showPupilTagging, setShowPupilTagging] = useState(false);
  const [pupilSearchTerm, setPupilSearchTerm] = useState("");
  const [loadingPupils, setLoadingPupils] = useState(false);

  // ✅ NEW: Year group expansion state
  const [expandedYears, setExpandedYears] = useState([]);
  const [allStudents, setAllStudents] = useState([]);

  // ✅ NEW: Text formatting state
  const textareaRef = React.useRef(null);

  const teacherEmail = localStorage.getItem("teacherEmail");
  const teacherId = 1; // You'll need to get this from your auth system

  // Fetch existing posts
  useEffect(() => {
    fetchPosts();
  }, []);

  // Fetch students for dynamic year-form mapping
  useEffect(() => {
    fetch(buildApiUrl('/synced-students'))
      .then(res => res.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : (data?.students || []);
        setAllStudents(arr);
      })
      .catch(err => console.error('Error loading students for forms:', err));
  }, []);

  // Update your existing fetchPosts function to filter by teacher
  const fetchPosts = async () => {
    try {
      // Fetch posts by specific teacher (you can add this endpoint or filter client-side)
      const response = await axios.get(buildApiUrl(`/api/posts/teacher/${teacherId}`));
      setPosts(response.data);
    } catch (err) {
      console.error("Error fetching posts:", err);
      // Fallback to all posts if teacher-specific endpoint doesn't exist
      try {
        const allPosts = await axios.get(buildApiUrl("/api/posts"));
        // Filter by teacher ID client-side
        const teacherPosts = allPosts.data.filter(post => post.teacher_id === teacherId);
        setPosts(teacherPosts);
      } catch (fallbackErr) {
        console.error("Error fetching all posts:", fallbackErr);
      }
    }
  };

  // ✅ NEW: Fetch available pupils
  useEffect(() => {
    const fetchPupils = async () => {
      setLoadingPupils(true);
      try {
        const response = await fetch(buildApiUrl("/api/posts/pupils/search"));
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        setAvailablePupils(Array.isArray(data.pupils) ? data.pupils : []); // ✅ Ensure it's an array
        console.log("📚 Loaded pupils:", data.pupils?.length || 0);
      } catch (error) {
        console.error("❌ Error loading pupils:", error.message);
        setAvailablePupils([]); // ✅ Avoid crash with empty array
      } finally {
        setLoadingPupils(false);
      }
    };
    
    fetchPupils();
  }, []);

  // ✅ NEW: Search pupils when search term changes
  useEffect(() => {
    if (pupilSearchTerm.length >= 2) {
      const searchPupils = async () => {
        try {
          const response = await fetch(
            buildApiUrl(`/api/posts/pupils/search?q=${encodeURIComponent(pupilSearchTerm)}`)
          );
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const data = await response.json();
          setAvailablePupils(Array.isArray(data.pupils) ? data.pupils : []); // ✅ Ensure it's an array
        } catch (error) {
          console.error("❌ Pupil search error:", error.message);
          setAvailablePupils([]); // ✅ Avoid crash
        }
      };
      
      const timeoutId = setTimeout(searchPupils, 300); // Debounce search
      return () => clearTimeout(timeoutId);
    } else if (pupilSearchTerm === "") {
      // Reset to all pupils when search is cleared
      fetch(buildApiUrl("/api/posts/pupils/search"))
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          return res.json();
        })
        .then(data => setAvailablePupils(Array.isArray(data.pupils) ? data.pupils : []))
        .catch(error => {
          console.error("❌ Reset pupils error:", error.message);
          setAvailablePupils([]); // ✅ Avoid crash
        });
    }
  }, [pupilSearchTerm]);

  // ✅ NEW: Helper functions
  const getFilteredPupils = () => {
    if (!Array.isArray(availablePupils)) {
      console.warn("⚠️ availablePupils is not an array:", availablePupils);
      return [];
    }
    
    let filtered = availablePupils;
    
    // Filter by selected forms (if any forms are selected)
    if (Array.isArray(selectedForms) && selectedForms.length > 0) {
      filtered = filtered.filter(pupil => selectedForms.includes(pupil.form));
    }
    
    return filtered.sort((a, b) => a.full_name.localeCompare(b.full_name));
  };

  const togglePupilTag = (schoolId) => {
    setTaggedPupils(prev => 
      prev.includes(schoolId)
        ? prev.filter(id => id !== schoolId)
        : [...prev, schoolId]
    );
  };

  const tagAllInSelectedForms = () => {
    if (!Array.isArray(availablePupils) || !Array.isArray(selectedForms)) {
      console.warn("⚠️ Invalid data for tagging:", { availablePupils, selectedForms });
      return;
    }
    
    const formPupils = availablePupils
      .filter(pupil => selectedForms.includes(pupil.form))
      .map(pupil => pupil.school_id);
    
    setTaggedPupils(prev => [...new Set([...prev, ...formPupils])]);
  };

  const clearAllTags = () => {
    setTaggedPupils([]);
  };

  // ✅ Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'size': ['10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px'] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'size', 'bold', 'italic', 'underline', 'color', 'background', 'align'
  ];

  // Add custom CSS for font sizes
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
        content: '10px';
        font-size: 10px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before {
        content: '11px';
        font-size: 11px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
        content: '12px';
        font-size: 12px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before {
        content: '14px';
        font-size: 14px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before {
        content: '16px';
        font-size: 16px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
        content: '18px';
        font-size: 18px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before {
        content: '20px';
        font-size: 20px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
        content: '24px';
        font-size: 24px !important;
      }
      .ql-editor .ql-size-10px { font-size: 10px; }
      .ql-editor .ql-size-11px { font-size: 11px; }
      .ql-editor .ql-size-12px { font-size: 12px; }
      .ql-editor .ql-size-14px { font-size: 14px; }
      .ql-editor .ql-size-16px { font-size: 16px; }
      .ql-editor .ql-size-18px { font-size: 18px; }
      .ql-editor .ql-size-20px { font-size: 20px; }
      .ql-editor .ql-size-24px { font-size: 24px; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that target audience is selected (null = nothing selected, [] = whole school)
    if (selectedForms === null || selectedForms === undefined) {
      setError("⚠️ Please select target classes or choose 'Post to Whole School'");
      setTimeout(() => setError(""), 3000);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("content", content);
      formData.append("category", category);
      
      // ✅ Improved created_by logic with better fallbacks
      const createdBy = authorInfo?.email || 
                       authorInfo?.name || 
                       localStorage.getItem('teacherEmail') || 
                       localStorage.getItem('userEmail') ||
                       "Hisham Shehadeh"; // Better default than generic email
      
      formData.append("created_by", createdBy);
      formData.append("target_forms", selectedForms ? selectedForms.join(",") : "");
      formData.append("tagged_pupils", taggedPupils.join(","));
      formData.append("media_urls", ""); // ✅ Keep this for backend compatibility

      // ✅ Add file uploads with proper key name
      if (mediaFiles && mediaFiles.length > 0) {
        mediaFiles.forEach((file) => {
          formData.append("files", file); // ✅ Backend expects 'files'
        });
      }

      console.log("📤 Sending post data:", {
        title,
        content,
        category,
        target_forms: selectedForms ? selectedForms.join(",") : "",
        tagged_pupils: taggedPupils.join(","),
        files: mediaFiles.length
      });

      const response = await fetch(buildApiUrl("/api/posts"), {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        // Reset form including new tagging fields
        setTitle("");
        setContent("");
        setCategory("school-announcement");
        setSelectedForms([]);
        setMediaFiles([]);          // ✅ Reset media files
        setTaggedPupils([]);
        setShowPupilTagging(false);
        setPupilSearchTerm("");
        
        if (onPostCreated) onPostCreated();
        if (onClose) onClose();
        
        alert("✅ Post created successfully!");
      } else {
        throw new Error("Failed to create post");
      }

    } catch (error) {
      console.error("❌ Error creating post:", error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormToggle = (form) => {
    setSelectedForms(prev => 
      prev && prev.includes(form) 
        ? prev.filter(f => f !== form)
        : [...(prev || []), form]
    );
  };

  // ✅ Dynamic year group structure built from actual student data
  const yearGroupsStructure = React.useMemo(() => {
    if (!allStudents || allStudents.length === 0) return [];
    const yearFormMap = {};
    allStudents.forEach(s => {
      const label = getYearLabel(s.nc_year || s.ncYear);
      const form = s.form;
      if (label && form) {
        if (!yearFormMap[label]) yearFormMap[label] = new Set();
        yearFormMap[label].add(form);
      }
    });
    const order = ['Pre-School', 'Preschool', 'Reception', ...Array.from({length: 13}, (_, i) => `Year ${i+1}`)];
    return order
      .filter(name => yearFormMap[name])
      .map(name => ({
        key: name === 'Pre-School' || name === 'Preschool' ? 'PS' : name === 'Reception' ? 'R' : name.replace('Year ', ''),
        name: name,
        forms: [...yearFormMap[name]].sort()
      }));
  }, [allStudents]);

  // ✅ NEW: Toggle year expansion
  const toggleYear = (year) => {
    setExpandedYears(prev =>
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
    );
  };

  // ✅ NEW: Select all forms in a year
  const selectAllInYear = (yearForms) => {
    const allFormsSelected = yearForms.every(form => selectedForms?.includes(form));
    if (allFormsSelected) {
      // Deselect all forms in this year
      setSelectedForms(prev => prev?.filter(f => !yearForms.includes(f)) || []);
    } else {
      // Select all forms in this year
      setSelectedForms(prev => [...new Set([...(prev || []), ...yearForms])]);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(file => file != null); // Filter out null/undefined
    setMediaFiles(prev => [...(prev || []), ...selectedFiles]); // ✅ Append new files instead of replacing
  };

  const removeFile = (indexToRemove) => {
    setMediaFiles(prev => prev.filter((file, index) => file != null && index !== indexToRemove)); // Also filter nulls
  };

  const deletePost = async (postId) => {
    if (!confirm("Are you sure you want to delete this post?")) return;

    try {
      await axios.delete(buildApiUrl(`/api/posts/${postId}`));
      fetchPosts();
      setSuccess("Post deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting post:", err);
      setSuccess("Failed to delete post");
    }
  };

  // ✅ ADD enhanced file type checking functions:
  const getFileIcon = (fileName) => {
    if (!fileName) return '📎'; // Return generic icon if fileName is null/undefined
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    switch (ext) {
      case 'pdf': 
        return '📄'; // PDF icon
      case 'doc':
      case 'docx': 
        return '📝'; // Word document icon
      case 'xls':
      case 'xlsx': 
        return '📊'; // Excel spreadsheet icon
      case 'ppt':
      case 'pptx': 
        return '📺'; // PowerPoint presentation icon
      case 'txt': 
        return '�'; // Text file icon
      case 'zip':
      case 'rar':
      case '7z': 
        return '�️'; // Archive icon
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'bmp': 
        return '�️'; // Image icon
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv': 
        return '🎬'; // Video icon
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac': 
        return '🎵'; // Audio icon
      case 'html':
      case 'htm': 
        return '🌐'; // Web page icon
      case 'css': 
        return '�'; // CSS file icon
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx': 
        return '⚡'; // JavaScript/TypeScript icon
      case 'json': 
        return '📋'; // JSON file icon
      case 'xml': 
        return '📊'; // XML file icon
      case 'csv': 
        return '�'; // CSV file icon
      default: 
        return '📎'; // Generic file icon
    }
  };

  const isImageFile = (file) => {
    if (!file || !file.type) return false; // Add null check
    return file.type.startsWith('image/');
  };

  const isVideoFile = (file) => {
    if (!file || !file.type || !file.name) return false; // Add null check
    return file.type.startsWith('video/') || /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)$/i.test(file.name);
  };

  const isPdfFile = (file) => {
    if (!file || !file.type || !file.name) return false; // Add null check
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  };

  // Create blob preview URLs once per mediaFiles change; revoke old ones on cleanup
  useEffect(() => {
    const urls = mediaFiles.map(file => {
      if (!file) return null;
      if (file.type && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
        return URL.createObjectURL(file);
      }
      return null;
    });
    setPreviewUrls(urls);
    return () => {
      urls.forEach(url => url && URL.revokeObjectURL(url));
    };
  }, [mediaFiles]);

  const handleEmojiClick = (emojiData) => {
    setContent((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false); // Close picker after selection
  };

  // ✅ Debug logging temporarily
  useEffect(() => {
    console.log("🔍 Debug availablePupils:", {
      value: availablePupils,
      type: typeof availablePupils,
      isArray: Array.isArray(availablePupils),
      length: availablePupils?.length
    });
  }, [availablePupils]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Success/Error Messages */}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
              {success}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter post title..."
              />
            </div>

{/* Enhanced Content with Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content *
              </label>
              
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your announcement..."
                className="bg-white"
                style={{ height: '200px', marginBottom: '50px' }}
              />
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div className="relative mt-2 mb-4">
                <div className="absolute z-10 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <EmojiPicker 
                    onEmojiClick={handleEmojiClick}
                    width={300}
                    height={350}
                    previewConfig={{
                      showPreview: false
                    }}
                  />
                </div>
                {/* Overlay to close picker when clicking outside */}
                <div 
                  className="fixed inset-0 z-0" 
                  onClick={() => setShowEmojiPicker(false)}
                />
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowEmojiPicker((prev) => !prev)}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                😊 Add Emoji
              </button>
            </div>

            {/* Class Selection Block */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Classes (select one or more)
              </label>
              
              {/* Year Groups with Expandable Forms */}
              <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
                {yearGroupsStructure.map((yearGroup) => (
                  <div key={yearGroup.key} className="border-b border-gray-100 last:border-b-0">
                    {/* Year Header */}
                    <button
                      type="button"
                      onClick={() => toggleYear(yearGroup.key)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 flex items-center gap-2">
                        <span className="text-sm">{expandedYears.includes(yearGroup.key) ? '▼' : '▶'}</span>
                        {yearGroup.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                          {yearGroup.forms.filter(f => selectedForms?.includes(f)).length}/{yearGroup.forms.length}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            selectAllInYear(yearGroup.forms);
                          }}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        >
                          {yearGroup.forms.every(f => selectedForms?.includes(f)) ? '✓ All' : 'Select All'}
                        </button>
                      </div>
                    </button>

                    {/* Forms in Year */}
                    {expandedYears.includes(yearGroup.key) && (
                      <div className="bg-white px-6 py-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 py-2">
                          {yearGroup.forms.map((form) => (
                            <label key={form} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
                              <input
                                type="checkbox"
                                checked={selectedForms?.includes(form)}
                                onChange={() => handleFormToggle(form)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="font-medium text-gray-700">{form}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Show selection status */}
              <p className="text-xs text-gray-500 mt-2">
                {selectedForms && selectedForms.length > 0 
                  ? `Selected: ${selectedForms.join(", ")}` 
                  : "No classes selected"
                }
              </p>

              {/* Post to All School Button */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    const confirmAll = window.confirm("⚠️ Are you sure you want to post this to the entire school?");
                    if (confirmAll) {
                      setSelectedForms([]); // interpreted as whole school
                    }
                  }}
                  className={`text-sm px-4 py-2 rounded-md border ${
                    Array.isArray(selectedForms) && selectedForms.length === 0
                      ? "bg-blue-100 text-blue-800 border-blue-200"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  🌐 Post to Whole School
                </button>
              </div>

              {/* Audience Preview */}
              <div className="mt-3 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700">
                  {Array.isArray(selectedForms) && selectedForms.length === 0 
                    ? "📢 This post will be visible to the entire school"
                    : selectedForms && selectedForms.length > 0
                    ? `📚 This post will be visible to: ${selectedForms.join(", ")}`
                    : "⚠️ Please select target classes or choose 'Post to Whole School'"
                  }
                </p>
              </div>
            </div>

            {/* Pupil Tagging Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  🎯 Tag Specific Pupils (Optional)
                </label>
                <button
                  type="button"
                  onClick={() => setShowPupilTagging(!showPupilTagging)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showPupilTagging ? "Hide" : "Show"} Tagging
                </button>
              </div>
              
              {/* Tagged Pupils Summary */}
              {taggedPupils.length > 0 && (
                <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-blue-800">
                      📌 {taggedPupils.length} pupil(s) tagged
                    </p>
                    <button
                      type="button"
                      onClick={clearAllTags}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {taggedPupils.slice(0, 5).map(schoolId => {
                      const pupil = availablePupils.find(p => p.school_id === schoolId);
                      return pupil ? (
                        <span key={schoolId} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {pupil.full_name} ({pupil.form})
                        </span>
                      ) : null;
                    })}
                    {taggedPupils.length > 5 && (
                      <span className="text-xs text-blue-600">
                        +{taggedPupils.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            
              {/* Pupil Tagging Interface */}
              {showPupilTagging && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  {/* Search and Quick Actions */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <input
                      type="text"
                      value={pupilSearchTerm}
                      onChange={(e) => setPupilSearchTerm(e.target.value)}
                      placeholder="Search pupils by name, ID, or form..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    {selectedForms.length > 0 && (
                      <button
                        type="button"
                        onClick={tagAllInSelectedForms}
                        className="px-3 py-2 bg-blue-100 text-blue-800 rounded-md text-sm hover:bg-blue-200"
                      >
                        Tag all in selected forms
                      </button>
                    )}
                  </div>
                
                  {/* Loading State */}
                  {loadingPupils && (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Loading pupils...</p>
                    </div>
                  )}
                
                  {/* Pupils List */}
                  {!loadingPupils && (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {(() => {
                        const filteredPupils = getFilteredPupils();
                        if (!Array.isArray(filteredPupils) || filteredPupils.length === 0) {
                          return (
                            <p className="text-sm text-gray-500 text-center py-4">
                              {pupilSearchTerm ? "No pupils found matching your search" : 
                               selectedForms.length > 0 ? "No pupils found in selected forms" : 
                               "No pupils available"}
                            </p>
                          );
                        }
                        
                        return filteredPupils.map(pupil => (
                          <label
                            key={pupil.school_id}
                            className="flex items-center gap-2 p-2 hover:bg-white rounded text-sm cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={taggedPupils.includes(pupil.school_id)}
                              onChange={() => togglePupilTag(pupil.school_id)}
                              className="text-blue-600"
                            />
                            <div className="flex-1">
                              <span className="font-medium">{pupil.full_name}</span>
                              <span className="text-gray-500 ml-2">
                                ({pupil.school_id} • {pupil.form})
                              </span>
                            </div>
                          </label>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ✅ NEW: Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="school-announcement">📢 School Announcement</option>
                <option value="school-events">🎉 School Events</option>
                <option value="trips">🚌 Trips</option>
                <option value="weekly-letters">📝 Weekly Letters</option>
                <option value="daily-activity">📅 Daily Activity</option>
              </select>
            </div>

            {/* ✅ NEW: File Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                📎 Attachments (Optional)
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supported: Images, Videos, PDF, Word, Excel, PowerPoint, Text files (Max 10MB each)
              </p>
              
              {/* ✅ File Preview Section */}
              {mediaFiles && mediaFiles.length > 0 && (
                <div className="mt-4 space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">📂 Selected Files:</h4>
                  
                  {/* ✅ Display Images at the top */}
                  <div className="space-y-2">
                    {mediaFiles.filter(file => file && isImageFile(file)).map((file, index) => (
                      <div key={`image-${index}`} className="relative">
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">🖼️</span>
                              <span className="text-sm font-medium text-gray-700">{file.name}</span>
                              <span className="text-xs text-gray-500">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(mediaFiles.indexOf(file))}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              ✕ Remove
                            </button>
                          </div>
                          {/* ✅ Image Preview */}
                          <div className="mt-2">
                            <img
                              src={previewUrls[mediaFiles.indexOf(file)] || ''}
                              alt={file.name}
                              loading="lazy"
                              className="max-w-full h-auto max-h-48 rounded-md border border-gray-200"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* ✅ Display Videos */}
                  <div className="space-y-2">
                    {mediaFiles.filter(file => file && isVideoFile(file)).map((file, index) => (
                      <div key={`video-${index}`} className="relative">
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">🎬</span>
                              <span className="text-sm font-medium text-gray-700">{file.name}</span>
                              <span className="text-xs text-gray-500">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(mediaFiles.indexOf(file))}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              ✕ Remove
                            </button>
                          </div>
                          {/* ✅ Video Preview */}
                          <div className="mt-2">
                            <video
                              src={previewUrls[mediaFiles.indexOf(file)] || ''}
                              controls
                              className="max-w-full h-auto max-h-48 rounded-md border border-gray-200"
                              preload="metadata"
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* ✅ Display PDFs and other files */}
                  <div className="space-y-2">
                    {mediaFiles.filter(file => file && !isImageFile(file) && !isVideoFile(file)).map((file, index) => (
                      <div key={`file-${index}`} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getFileIcon(file.name)}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-700">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {isPdfFile(file) ? 'PDF Document' : 'File'} • {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(mediaFiles.indexOf(file))}
                            className="text-red-600 hover:text-red-800 text-sm px-2 py-1 rounded hover:bg-red-50"
                          >
                            ✕ Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? "Creating..." : (() => {
                  let label = "📝 Create Post";
                  if (taggedPupils.length > 0) label += ` (${taggedPupils.length} tagged)`;
                  if (mediaFiles.length > 0) label += ` + ${mediaFiles.length} file${mediaFiles.length !== 1 ? 's' : ''}`;
                  return label;
                })()}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePost;
